'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';

import UiLogo from '@/components/ui/UiLogo';
import { UiButton } from '@/components/ui/UiButton';
import { UiIcon } from '@/components/ui/UiIcon';
import UiModal from '@/components/ui/UiModal/UiModal';
import { useSiteSettings } from '@/components/providers';
import { useScrollSpy } from '@/hooks';
import { telHref } from '@/lib/utils/contact';
import { LinkLabel } from './LinkLabel';
import { NavLink } from './NavLink';
import { getAnchorIds, getHeaderCtas, isNavItemActive } from './navigation.utils';

type NavigationOffCanvasProps = {
    open: boolean;
    onClose: () => void;
    /** The header's scroll state — the head mirrors the bar's padding. */
    isScrolled?: boolean;
};

/**
 * Full-screen navigation below the header breakpoint. A flex column: head and
 * links scroll together, the action bar at the bottom stays put.
 */
export function NavigationOffCanvas({ open, onClose, isScrolled = false }: NavigationOffCanvasProps) {
    const settings = useSiteSettings();
    const pathname = usePathname();

    const items = useMemo(() => settings.navigation?.main ?? [], [settings.navigation?.main]);
    const anchorIds = useMemo(() => getAnchorIds(items), [items]);
    const activeAnchorId = useScrollSpy({ ids: anchorIds });

    const phone = settings.contactData?.contactPhone;
    const whatsapp = settings.contactData?.contactWhatsapp;
    const ctas = getHeaderCtas(settings.header);
    const addressNote = settings.header?.menuAddressNote;

    return (
        <UiModal
            open={open}
            onClose={() => onClose()}
            size="full"
            hideClose
            containerClassName="!p-0 !min-h-screen !items-stretch md:!items-stretch"
            panelClassName="!size-full !h-screen !p-0 !rounded-none bg-(--bg) flex flex-col overflow-hidden"
        >
            <div className="site-offcanvas__scroll">
                <div
                    className="site-header-container site-offcanvas__head flex shrink-0 items-center justify-between gap-[22px]"
                    data-scrolled={isScrolled}
                >
                    <Link
                        href="/"
                        onClick={onClose}
                        aria-label={settings.siteTitle}
                        className="site-logo-link site-offcanvas__logo inline-flex max-w-full"
                    >
                        <UiLogo variant="wordmark" className="site-logo" />
                    </Link>

                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Menü schließen"
                        className="shape-pill flex size-11 shrink-0 cursor-pointer items-center justify-center border border-(--border) bg-(--surface) text-(--text)"
                    >
                        <UiIcon name="x-mark" size={22} aria-hidden="true" />
                    </button>
                </div>

                <div className="site-header-container pb-(--sp-sm)">
                    <nav className="mt-[26px] flex flex-col gap-0.5" aria-label="Hauptnavigation">
                        {items.map((item) => (
                            <NavLink
                                key={item._key}
                                item={item}
                                isActive={isNavItemActive(item, pathname, activeAnchorId)}
                                onClick={onClose}
                                className="site-offcanvas__link flex items-center justify-between border-b border-(--border) px-1 py-[18px] text-left text-(--text)"
                            >
                                <span>
                                    <LinkLabel item={item} />
                                </span>
                                <UiIcon
                                    name="arrow-right"
                                    size={18}
                                    className="site-offcanvas__arrow"
                                    aria-hidden="true"
                                />
                            </NavLink>
                        ))}
                    </nav>

                    {phone || whatsapp ? (
                        <div className="site-offcanvas__actions mt-[26px] flex gap-3">
                            {phone ? (
                                <a
                                    href={telHref(phone)}
                                    className="btn btn-primary btn-lg shape-pill flex-1 justify-center"
                                >
                                    <UiIcon name="phone" size={18} aria-hidden="true" />
                                    Anrufen
                                </a>
                            ) : null}

                            {whatsapp ? (
                                <a
                                    href={whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-dark-outline btn-lg shape-pill flex-1 justify-center"
                                >
                                    <UiIcon name="whatsapp" size={18} className="text-(--accent)" aria-hidden="true" />
                                    WhatsApp
                                </a>
                            ) : null}
                        </div>
                    ) : null}

                    {addressNote ? (
                        <p className="mt-5 text-sm whitespace-pre-line text-(--muted)">{addressNote}</p>
                    ) : null}
                </div>
            </div>

            {/* One bar per page tone, the same button as in the header; CSS shows
                the one matching the page (header.css), so a tone without its
                own button shows no bar at all. */}
            {ctas.map(({ tone, item }) => (
                <div key={tone} className="site-offcanvas__bar" data-tone={tone}>
                    <div className="site-header-container">
                        <UiButton
                            item={item}
                            size="lg"
                            onClick={onClose}
                            className="site-offcanvas__cta w-full justify-center"
                        />
                    </div>
                </div>
            ))}
        </UiModal>
    );
}
